import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { getAllFields, getFields } from '../store/actions/fieldsActions';
import { getNotifications } from '../store/actions/notificationsActions';
import { filterUser } from "../store/actions/usersActions";
import { getStatusColor, isCompanyAdmin, isSuperAdmin } from '../helpers/helpers';
import './Dashboard.scss';

import Spinner from './_common/Spinner';
import MapWithPopups from "./MapWithPopups";
import SensorsMap from "./SensorsMap";
import FieldsList from "./FieldsList";
import NotificationsList from "./NotificationsList";
import FieldSensors from "./FieldSensors";
import MapView from "./Maps/MapView";

const selector = state => {
    const { language, fields, notifications, users } = state;
    return {
        t: language.translator,
        pendingFields: fields.pending,
        fields: fields.fields,
        pendingNotifications: notifications.pending,
        notifications: notifications.notifications,
        filteredUser: users.filtered
    }
};

const Dashboard = () => {
    const { t, pendingFields, fields, pendingNotifications, notifications, filteredUser } = useSelector(selector);
    const dispatch = useDispatch();
    const [dashboardTopExpanded, setDashboardTopExpanded] = useState(false);
    const [showSensors, setShowSensors] = useState(false);
    const [selectedField, setSelectedField] = useState({});

    useEffect(() => {
        if(isCompanyAdmin() || isSuperAdmin()) {
            dispatch(filterUser({}));
            dispatch(getAllFields());
            dispatch(getNotifications());
        } else {
            dispatch(getFields());
            dispatch(getNotifications());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if(pendingFields || pendingNotifications) {
        return (
            <div className='page'>
                <Spinner size={300}/>
            </div>
        );
    }

    //@TODO Remove console logs. Add notifications pagination.
    // console.log(notifications)
    // console.log([notifications[0], notifications[1], notifications[2]])

    let fieldsMarkers = []

    fields.forEach(field => {
        if (field.lat && field.lon) {
            fieldsMarkers.push(
                {
                    lat: field.lat,
                    lng: field.lon,
                    color: getStatusColor(field.temperatureStatus),
                    name: field.name,
                    averageTemperature: field.averageTemperature,
                    id: field.id,
                    sensors: field.sensors
                }
            )
        }
    });

    let sensorMarkers = [];

    if(showSensors) {
        selectedField.sensors.forEach(sensor => {
            if (sensor.lat && sensor.lon) {
                sensorMarkers.push(
                    {
                        lat: sensor.lat,
                        lng: sensor.lon,
                        color: getStatusColor(sensor.temperatureStatus),
                        name: sensor.name,
                        loraIdentifier: sensor.loraIdentifier,
                        temperature: sensor.temperature,
                        humidity: sensor.measurements[0].humidity,
                        date: sensor.measurements[0].dateTimeCreated.replace('T'),
                        id: sensor.id
                    }
                )
            }
        });
    }

    const handleShowSensors = fieldId => {
        setShowSensors(true);
        setSelectedField(fields.find(x => x.id === fieldId));
        setDashboardTopExpanded(false);
    }

    const handleHideSensors = () => {
        setShowSensors(false);
        setSelectedField({});
    }

    return (
        <div className='dashboard'>

            <div className={dashboardTopExpanded ? 'dashboardMap dashboardMap--expanded' : 'dashboardMap'}>
                {!showSensors
                    ?
                    <MapWithPopups
                        zoom={5.8}
                        center={fieldsMarkers.length ? fieldsMarkers[0] : {lat: 52.2297, lng: 21.0122}}
                        markers={fieldsMarkers}
                        handleLinkClick={handleShowSensors}
                        mapType={'fields'}
                    />
                    // <MapWithPopups
                    //     defaultZoom={5.8}
                    //     defaultCenter={fieldsMarkers.length ? fieldsMarkers[0] : {lat: 52.2297, lng: 21.0122}}
                    //     markers={fieldsMarkers}
                    //     handleLinkClick={handleShowSensors}
                    // />
                    :
                    <MapWithPopups
                        mapType={'sensors'}
                        zoom={10}
                        center={sensorMarkers.length ? sensorMarkers[0] : {lat: 52.2297, lng: 21.0122}}
                        markers={sensorMarkers}
                    />
                }
                <div
                    className='dashboardTopExpandButton'
                    onClick={() => setDashboardTopExpanded(!dashboardTopExpanded)}
                >
                    <span className='material-icons dashboardTopExpandButtonIcon'>
                        {dashboardTopExpanded ? 'expand_less' : 'expand_more'}
                    </span>
                </div>
            </div>
            <div className={!dashboardTopExpanded ? 'dashboardBottom' : 'dashboardBottom dashboardBottom--collapsed'}>
                {!showSensors &&
                    <div
                        className={(
                            (Object.keys(filteredUser).length !== 0 &&
                            filteredUser.constructor === Object &&
                            (isSuperAdmin() || isCompanyAdmin()))
                            || (!isSuperAdmin() && !isCompanyAdmin())
                        )
                            ? `fieldsBox ${dashboardTopExpanded ? 'fieldsBox--collapsed' : ''}`
                            : `fieldsBox fieldsBox--full ${dashboardTopExpanded ? 'fieldsBox--collapsed' : ''}`
                        }
                        // className={!dashboardTopExpanded ? 'fieldsBox' : 'fieldsBox fieldsBox--collapsed'}
                    >
                        {!fields.length
                            ? <Spinner size={150}/>
                            : <FieldsList
                                fields={fields}
                                page={1}
                                pageCount={1}
                                itemsCount={fields.length}
                            />
                        }
                    </div>
                }
                {!showSensors &&
                    <div
                        className={(
                            Object.keys(filteredUser).length === 0 &&
                            filteredUser.constructor === Object &&
                            (isSuperAdmin() || isCompanyAdmin())
                        )
                            ? `notificationsBox ${dashboardTopExpanded ? 'notificationsBox--collapsed' : ''}`
                            : `notificationsBox notificationsBox--visible ${dashboardTopExpanded ? 'notificationsBox--collapsed' : ''}`
                        }
                    >
                        {pendingNotifications
                            ? <Spinner size={150}/>
                            : <NotificationsList
                                notifications={notifications}
                                title={t.gettext('Notifications')}
                                // viewAllButton
                                user={filteredUser}
                            />
                        }
                    </div>
                }
                {showSensors &&
                    <div className={!dashboardTopExpanded ? 'sensorsBox' : 'sensorsBox sensorsBox--collapsed'}>
                        {!selectedField
                            ? <Spinner size={150}/>
                            : <FieldSensors
                                field={selectedField}
                                backMethod={handleHideSensors}
                            />
                        }
                    </div>
                }
            </div>
        </div>
    );
};

export default Dashboard;
